import { modal } from '@repo/ui';
import { AutomationExecutionNodeStatusEnum, AutomationStatus } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import { NodeType } from 'automations/data/nodeTypes';
import useActionNodeConfig from 'automations/hooks/actionNodes/useActionNodeConfig';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { useConfig } from 'automations/hooks/useConfig';
import { automationStatusSelector } from 'automations/selectors/automationConfig';
import { childrenCountSelector } from 'automations/selectors/reactFlow';
import { addNodeWithPosition } from 'automations/store/reactFlowSlice';
import { type ActionNodeType } from 'automations/types/nodeSchemas/actionNodes';
import { deleteNode } from 'automations/utils/deleteNode';
import { generateNewNodePayload, getNodeDisplayTitle } from 'automations/utils/node';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import NodeUi from '../NodeUi';
import styles from './ActionNode.module.scss';

export default function ActionNode(node: NodeProps<ActionNodeType>) {
  const { id, data } = node;

  const dispatch = useAutomationDispatch();
  const childrenCount = useSelector(childrenCountSelector(id));
  const { setMenuOpen } = useCommonNodeHandles(node);

  const automationStatus = useSelector(automationStatusSelector);
  const targetExecStatus = node?.data?.executionStatus;

  const backgroundStatus = useMemo(() => {
    if (targetExecStatus === AutomationExecutionNodeStatusEnum.SUCCESS) return 'success';
    if (targetExecStatus === AutomationExecutionNodeStatusEnum.FAILED) return 'failed';
    return undefined;
  }, [targetExecStatus]);

  const { actions } = useConfig();

  const {
    subTitle,
    icon: Icon,
    hasErrors,
    errorMessages,
    handleCopy,
    AdditionalUI,
  } = useActionNodeConfig(node);

  const actionContent = useMemo(() => actions?.find(
    (a) => a.name === data.nodeSubType,
  ), [data.nodeSubType, actions]);

  const handleAddNode = useCallback(() => {
    const newNode = generateNewNodePayload({
      nodeType: NodeType.newBlock,
      data: {
        executionOrder: childrenCount + 1,
      },
    });
    dispatch(addNodeWithPosition({
      newNode,
      sourceNodeId: id,
    }));
  }, [childrenCount, dispatch, id]);

  const handleDeleteNode = useCallback(() => {
    modal.confirm({
      title: 'Are you sure you want to delete this node?',
      content: 'NOTE: This action cannot be undone, All child nodes of this node will also be deleted.',
      centered: true,
      width: 600,
      onOk: () => dispatch(deleteNode(id)),
      okText: 'Delete',
      okButtonProps: {
        danger: true,
      },
    });
  }, [dispatch, id]);

  const canDeleteNode = automationStatus === AutomationStatus.draft;

  return (
    <NodeUi
      nodeId={id}
      nodeTitle={getNodeDisplayTitle(data.nodeSubType)}
      nodeSubtitle={subTitle ?? actionContent?.desc}
      icon={{
        iconContent: <Icon className={styles.iconContent} />,
        iconBgClassName: styles.iconBg,
      }}
      targetHandle={{
        onClick: handleAddNode,
      }}
      hasErrors={hasErrors}
      errorMessages={errorMessages}
      sourceHandle={{}}
      contextMenu={{
        onEdit: () => setMenuOpen(true),
        onCopy: handleCopy,
        onDelete: canDeleteNode ? handleDeleteNode : undefined,
      }}
      AdditionalUI={AdditionalUI}
      debugContent={data}
      successExecutionCounts={data?.successExecutionCounts}
      backgroundStatus={backgroundStatus}
    />
  );
}
